/**
 * FormSubmissionPage — standalone route wrapper around {@link FormFillPanel}.
 *
 * Adds: breadcrumb header + navigate-back on submit. All form rendering,
 * validation, save/submit logic, soft-warning lifecycle, version-upgrade
 * dialog, and comment threading lives in the shared panel — never duplicate
 * it here.
 */
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import { supabase } from '@/lib/supabase/client'
import { FormFillPanel } from '../components/form-fill-panel'

function parseTitle(v: unknown): string {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object') return (v as Record<string, string>).fa ?? ''
  return ''
}

export function FormSubmissionPage() {
  const { projectId, recordId, formId } = useParams<{
    projectId: string
    recordId: string
    formId: string
  }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const submissionId = searchParams.get('submissionId') ?? undefined

  // Breadcrumb context: project name + record display_id + form title.
  const { data: crumbs } = useQuery({
    queryKey: ['form-breadcrumb', projectId, recordId, formId],
    queryFn: async () => {
      const [proj, rec, form] = await Promise.all([
        supabase.from('projects').select('name').eq('id', projectId!).single(),
        supabase.from('records').select('display_id').eq('id', recordId!).single(),
        supabase.from('forms').select('title').eq('id', formId!).single(),
      ])
      return {
        projectName: proj.data?.name ?? '',
        recordDisplayId: rec.data?.display_id ?? recordId!,
        formTitle: parseTitle(form.data?.title),
      }
    },
    enabled: !!projectId && !!recordId && !!formId,
    staleTime: 60_000,
  })

  if (!projectId || !recordId || !formId) return null

  const recordUrl = `/projects/${projectId}/records/${recordId}`
  const projectUrl = `/projects/${projectId}/records`

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-border px-6 py-3 shrink-0">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Link to={projectUrl} className="hover:text-foreground transition-colors flex items-center gap-1.5">
            <ArrowRight className="w-3.5 h-3.5" />
            {crumbs?.projectName ?? 'پروژه'}
          </Link>
          <span>/</span>
          <Link to={recordUrl} className="hover:text-foreground transition-colors font-mono">
            {crumbs?.recordDisplayId ?? '...'}
          </Link>
          <span>/</span>
          <span className="text-foreground">{crumbs?.formTitle ?? '...'}</span>
        </div>
      </div>

      <div className="flex-1 min-h-0">
        <FormFillPanel
          projectId={projectId}
          recordId={recordId}
          formId={formId}
          submissionId={submissionId}
          onSaved={(submitted) => {
            if (submitted) navigate(recordUrl)
          }}
        />
      </div>
    </div>
  )
}
